import {
  ITransaction,
  TransactionType
} from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import { ITransactionGroup } from "@wingspanhq/bookkeeping/dist/lib/interfaces/insights";
import {
  WSButton,
  WSButtons,
  WSLoader
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import chunk from "lodash/chunk";
import flatten from "lodash/flatten";
import React, { useState } from "react";
import { Redirect, Route, useHistory } from "react-router-dom";
import { BrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { ProjectOnboardingLayout } from "../../../components/ProjectOnboardingLayout/ProjectOnboardingLayout";
import { useMutationUpdateTransaction } from "../../../query/bookkeeping/mutations";
import { useQueryCatchUpTransactionsExpense } from "../../../query/bookkeeping/queries";
import { getTotalCatchUpExpenseTransactions } from "../../../query/bookkeeping/selectors";
import { useUserId } from "../../../query/hooks/helpers";
import { useMutationUpdateActivityCatchUpEvents } from "../../../query/users/mutations";
import {
  useQueryAccounts,
  useQueryActivities
} from "../../../query/users/queries";
import { WSQueries } from "../../../query/WSQuery";
import { track } from "../../../utils/analytics";
import {
  getCatchUpPagesFromGroups,
  getCatchUpTransactionsFromGroups,
  totalTransactionValues,
  updateTransactions
} from "../../utils";
import { CatchUpLayout } from "./CatchUpLayout";
import { CatchUpTransactionForm } from "./CatchUpTransactionForm";
import styles from "./Index.module.scss";
import { CatchUpTransactionDetails } from "./CatchUpTransaction";
import { useQueriesOn } from "../../../shared/hooks/useQueriesOn";

export const CatchUpExpenseEdit: React.FC<{
  next: string;
}> = ({ next }) => {
  const history = useHistory();

  const memberId = useUserId();
  const [updateActivityCatchUpEvents] =
    useMutationUpdateActivityCatchUpEvents();
  const [updateTransaction] = useMutationUpdateTransaction();
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState<ITransactionGroup[][]>([]);
  const [oldTransactions, setOldTransactions] = useState<ITransaction[]>([]);
  const [loading, setLoading] = useState(false);

  const qAccounts = useQueryAccounts();
  const qActivity = useQueryActivities(memberId);
  const qCatchUpTransactionsExpense =
    useQueryCatchUpTransactionsExpense(memberId);

  useQueriesOn(
    "successAll",
    ({ data: activity }, qAccounts, { data: catchUpTransactionsExpense }) => {
      setPages(
        getCatchUpPagesFromGroups(
          catchUpTransactionsExpense.merchantExpenses,
          catchUpTransactionsExpense.accountExpenses,
          activity.events.expensesReviewedAt
        )
      );

      setOldTransactions(
        getCatchUpTransactionsFromGroups(
          catchUpTransactionsExpense.merchantExpenses,
          catchUpTransactionsExpense.accountExpenses
        )
      );
    },
    qActivity,
    qAccounts,
    qCatchUpTransactionsExpense
  );

  return (
    <WSQueries
      queries={{
        qActivity,
        qAccounts,
        qCatchUpTransactionsExpense
      }}
      renderLoader={() => (
        <ProjectOnboardingLayout progress={0} noBack>
          <WSLoader.Spinner style={{ marginTop: 100 }} />
        </ProjectOnboardingLayout>
      )}
    >
      {({ qAccounts, qCatchUpTransactionsExpense, qActivity }) => {
        const totalAmount = totalTransactionValues(
          TransactionType.Expense,
          ...flatten(pages)
        );

        const groupsToReview = flatten(pages.slice(page + 1, Infinity)).length;
        const nextGroups = pages[page + 1]?.length;
        const countOfMerchantPages = chunk(
          qCatchUpTransactionsExpense.data.merchantExpenses,
          5
        ).length;

        const totalExpenseTransactions = getTotalCatchUpExpenseTransactions(
          qCatchUpTransactionsExpense.data,
          qActivity.data
        );

        if (totalExpenseTransactions === 0) {
          return <Redirect to={next} />;
        }

        return (
          <CatchUpLayout
            progress={0}
            steps={[
              {
                title: "Add up income",
                name: "1",
                checked: true
              },
              {
                title: "Find business expenses",
                name: "2",
                current: true
              }
            ]}
            onBack={
              page > 0
                ? () => {
                    setPage(page - 1);
                  }
                : undefined
            }
          >
            <Route
              path={"/member/bookkeeping/catch-up/expense/:transactionId"}
              component={CatchUpTransactionDetails}
              exact
            />
            <BrowserPageTitle title="Select business income" />
            <p className={styles.subtitle}>
              Total business expenses:{" "}
              {formatMoney(
                totalAmount,
                qCatchUpTransactionsExpense.data.accountExpenses[0]
                  .transactions[0].currency
              )}
            </p>
            <h1 className={styles.title}>Select business expenses</h1>
            <CatchUpTransactionForm
              type={TransactionType.Expense}
              transactionGroups={pages[page]}
              onChange={transactionGroups => {
                setPages(prev =>
                  prev.map((t, i) => {
                    if (i === page) {
                      return transactionGroups;
                    } else {
                      return t;
                    }
                  })
                );
              }}
              accounts={qAccounts.data}
              showCategory={page < countOfMerchantPages}
            />

            <p className={styles.subtitle}>
              {groupsToReview
                ? `${groupsToReview} groups to review`
                : "All groups reviewed"}{" "}
            </p>

            <WSButtons mt="XL" mb="2XL">
              {nextGroups ? (
                <WSButton
                  name="catchUpExpenseNext"
                  loading={loading}
                  onClick={async () => {
                    setLoading(true);
                    await updateTransactions(
                      pages[page],
                      oldTransactions,
                      TransactionType.Expense,
                      updateTransaction
                    );
                    setLoading(false);

                    setPage(prev => prev + 1);
                  }}
                >
                  Continue to next {pages[page + 1]?.length}
                </WSButton>
              ) : (
                <WSButton
                  name="catchUpExpenseFinish"
                  onClick={async () => {
                    setLoading(true);
                    await updateTransactions(
                      pages[page],
                      oldTransactions,
                      TransactionType.Expense,
                      updateTransaction
                    );
                    await updateActivityCatchUpEvents({
                      userId: memberId,
                      events: {
                        expensesReviewedAt: new Date()
                      }
                    });

                    setLoading(false);

                    track("Catch Up Completed", {
                      type: "incremental"
                    });

                    history.push(next);
                  }}
                  loading={loading}
                >
                  Finish expense review
                </WSButton>
              )}
              {pages.length > 3 && page >= 1 && page < countOfMerchantPages ? (
                <WSButton.Secondary
                  onClick={async () => {
                    track("Catch Up Completed", {
                      type: "incremental"
                    });

                    history.push(next);
                    setPage(countOfMerchantPages);
                  }}
                >
                  Skip to account view
                </WSButton.Secondary>
              ) : null}
            </WSButtons>
          </CatchUpLayout>
        );
      }}
    </WSQueries>
  );
};
